import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase/config';
import { buildFunctionUrl } from '../utils/functionsUrl';
import { buildAuthHeaders } from '../utils/authHeaders';

/**
 * 専用URL / QRコード（アクセスリンク）
 *
 * 合言葉そのものは Firestore の company_access_links に保存されており、
 * ルールでクライアントからの読み書きを全面禁止している。
 * 発行・一覧・失効・引き換えはすべて Cloud Functions を経由する。
 *
 * 設計書: レフリアメモ/認証の一本化について.md（11章）
 */

export type AccessLink = {
  token: string;
  label: string;
  url: string;
  createdAt: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
  useCount: number;
};

async function postWithAuth<T>(functionName: string, body: unknown): Promise<T> {
  const response = await fetch(buildFunctionUrl(functionName), {
    method: 'POST',
    headers: await buildAuthHeaders(),
    body: JSON.stringify(body ?? {}),
  });

  if (!response.ok) {
    let message: string | undefined;
    try {
      message = (await response.json())?.error;
    } catch {
      // JSON でない場合は既定のメッセージを使う
    }
    throw new Error(message || '処理に失敗しました');
  }

  return response.json() as Promise<T>;
}

/** 自社のアクセスリンク一覧を取得する（管理者用） */
export async function listAccessLinks(): Promise<AccessLink[]> {
  const result = await postWithAuth<{ links: AccessLink[] }>('listAccessLinks', {});
  return result.links ?? [];
}

/**
 * アクセスリンクを発行する（管理者用）。
 *
 * 会社ごとに1本の運用のため名前は指定しない。サーバー側が既定の名前を付ける。
 */
export async function createAccessLink(): Promise<AccessLink> {
  const result = await postWithAuth<{ link: AccessLink }>('createAccessLink', {});
  return result.link;
}

/**
 * 有効なアクセスリンクを用意する（管理者用）。
 *
 * 会社ごとに1本の運用のため「発行する」という操作は設けず、必要になった時点で
 * 自動的に作る。管理画面でQRを開いたときと、会社登録が完了したときに呼ぶ。
 */
export async function ensureAccessLink(): Promise<AccessLink[]> {
  const links = await listAccessLinks();
  if (links.some((link) => !link.revokedAt)) {
    return links;
  }

  const created = await createAccessLink();
  return [created, ...links];
}

/** アクセスリンクを失効させる（管理者用） */
export async function revokeAccessLink(token: string): Promise<void> {
  await postWithAuth('revokeAccessLink', { token });
}

/**
 * アクセスリンクを切り替える（管理者用）。
 * 同じ拠点名で新しいURLを発行し、古いURLを失効させる。
 */
export async function rotateAccessLink(token: string): Promise<AccessLink> {
  const result = await postWithAuth<{ link: AccessLink }>('rotateAccessLink', { token });
  return result.link;
}

/**
 * 管理者から投稿者（一般ユーザー）へ切り替える。
 *
 * 従来は会社パスワードを取得して共有アカウントにログインし直していたが、
 * 会社パスワードは QR/URL 方式では不要になるため、サーバーが発行する
 * カスタムトークンでサインインする形に変更した。
 */
export async function switchToCorporateUser(): Promise<void> {
  const result = await postWithAuth<{ customToken: string }>('issueCorporateToken', {});
  await signInWithCustomToken(auth, result.customToken);
}

export type RedeemResult = {
  companyId: string;
  corporateCode: string;
  companyName: string;
};

/**
 * 合言葉でログインする（QRを読み取った利用者が実行する）。
 *
 * サーバーが合言葉を検証してカスタムトークンを返し、それでサインインすることで
 * 会社IDログインと同じ状態になる。
 */
export async function signInWithAccessLink(token: string): Promise<RedeemResult> {
  const response = await fetch(buildFunctionUrl('redeemAccessLink'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    let message: string | undefined;
    try {
      message = (await response.json())?.error;
    } catch {
      // JSON でない場合は既定のメッセージを使う
    }
    throw new Error(message || 'このURLは使用できません。');
  }

  const result = await response.json();
  await signInWithCustomToken(auth, result.customToken);

  return {
    companyId: result.companyId,
    corporateCode: result.corporateCode,
    companyName: result.companyName ?? '',
  };
}
